import { useMemo } from 'react'
import { useQueries } from '@tanstack/react-query'
import useApi from '../../../../hooks/useApi'
import { collectSources, resolveMeasures } from './measures'
import { rangeParams } from './period'

/*
 * Reads every aggregate a widget's measures need, for its period and — when it
 * compares — for the period before, and resolves them into measures.
 *
 * Each source is one query, keyed by the source and its range, so two widgets
 * reading invoice/byPeriod for the same period share a request and a cache
 * entry. A changed period keeps showing the last answer until the new one is in
 * (`placeholderData`), so the chart does not drop to a skeleton on every pick.
 */
const fetchRows = async (getMe, source, params) => {
    const qs = new URLSearchParams({ ...(source.params ?? {}), ...params }).toString()
    const response = await getMe(`${source.entity}/agr/${source.aggregation}?${qs}`)
    if (Array.isArray(response?.data)) return response.data
    throw new Error(response?.error ?? `Could not read ${source.entity}/${source.aggregation}`)
}

const useAnalyticsData = (measures, period, { compare = true } = {}) => {
    const { getMe } = useApi()
    const sources = useMemo(() => collectSources(measures), [measures])

    const ranges = [{ id: 'current', range: period.range }]
    if (compare && period.previous) ranges.push({ id: 'previous', range: period.previous })

    const queries = ranges.flatMap(({ id, range }) => sources.map(source => {
        const params = rangeParams(range, period.grain)
        return {
            queryKey: ['analytics', source.key, params.fromDate, params.toDate, params.grain, params.timeZone],
            queryFn: () => fetchRows(getMe, source, params),
            placeholderData: (prev) => prev,
            staleTime: 60_000,
            meta: { rangeId: id, sourceKey: source.key },
        }
    }))

    const results = useQueries({ queries })

    return useMemo(() => {
        const rowsFor = (rangeId) => Object.fromEntries(queries
            .map((q, i) => [q, results[i]])
            .filter(([q]) => q.meta.rangeId === rangeId)
            .map(([q, r]) => [q.meta.sourceKey, r?.data ?? []]))

        const isLoading = results.some(r => r.isLoading)
        const error = results.find(r => r.isError)?.error ?? null
        return {
            isLoading,
            isFetching: results.some(r => r.isFetching),
            error,
            refetch: () => results.forEach(r => r.refetch()),
            current: isLoading ? null : resolveMeasures(measures, rowsFor('current'), period.keys),
            previous: isLoading || !ranges.find(r => r.id === 'previous') ? null : resolveMeasures(measures, rowsFor('previous'), period.previousKeys),
        }
    // `results` is a new array every render; its members' data is what changes
    }, [results.map(r => r.dataUpdatedAt).join(), results.map(r => r.status).join(), measures, period.keys, period.previousKeys])
}

export default useAnalyticsData
